import React, { useMemo } from 'react';

/* External */
import { Chip, IconButton, Tooltip, Box } from '@material-ui/core';
import moment from 'moment';
import { falsy, fNumber, getUnitDetails, truthty } from 'utils/functions';
import { useTranslation } from 'react-i18next';

/* Project */
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import LaunchIcon from '@material-ui/icons/Launch';
import ErrorIcon from '@material-ui/icons/Error';
import ListIcon from '@material-ui/icons/List';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { CalendarTodayOutlined, CategoryOutlined, PersonOutline } from '@material-ui/icons';
import { Row } from 'commons/components/DataTable/Row';
import { TruncateTextToolTip } from 'commons/components/DataTable/TruncateTextToolTip';
import BoxIcon from 'commons/assets/icons/box.svg';

function Item({
  classes,
  process,
  objects,
  openMenuHandler,
  processOnClick,
  headerList,
  isMobileDevice,
  suppliersByProcess,
}) {
  const { t } = useTranslation();
  const { unitCode } = getUnitDetails(objects.accounts);
  const isClosed = truthty(process.closedAt);

  const getNumber = () => (
    <div className="d-flex flex-direction-column align-items-start">
      <TruncateTextToolTip
        text={`#${process.id || '-'}`}
        style={{
          color: truthty(process.closedAt) ? '#e57373' : '#47AA4D',
          width: 100,
          paddingLeft: '10px',
        }}
      />
      <Chip
        label={isClosed ? t('Closed') : t('Open')}
        size="small"
        style={{
          backgroundColor: isClosed ? '#ffebee' : '#e8f5e8',
          color: isClosed ? '#c62828' : '#2e7d32',
          fontSize: '0.8rem',
          height: '1.2rem',
          marginTop: '0.4rem',
          fontWeight: 'bold',
          padding: '0.2rem',
        }}
      />
    </div>
  );

  const getSupplier = () => {
    // Si hay método custom (automatización), usarlo
    if (typeof process.customSupplier === 'function') {
      return <TruncateTextToolTip text={process.customSupplier()} />;
    }
    // Fallback al método original
    return truthty(suppliersByProcess[process.id]) ? (
      <TruncateTextToolTip text={`${suppliersByProcess[process.id]}`} />
    ) : (
      '-'
    );
  };

  const getLots = () => {
    // Si hay método custom (automatización), usarlo
    if (typeof process.customLotNumber === 'function') {
      return <TruncateTextToolTip text={process.customLotNumber()} />;
    }
    // Fallback al método original
    if (!process.entryLots || typeof process.entryLots !== 'function') {
      return <TruncateTextToolTip text="-" />;
    }
    return (
      <TruncateTextToolTip
        text={[...new Set(process.entryLots().flatMap((lot) => lot.lotNumbers()))].join(', ')}
        lines={3}
      />
    );
  };

  const getCategory = () => {
    if (!process.processCategories || typeof process.processCategories !== 'function') {
      return <TruncateTextToolTip text={process.ProcessCategory?.name || '-'} />;
    }
    return <TruncateTextToolTip text={process.processCategories()?.name || '-'} />;
  };

  const getProcessType = () => {
    if (!process.processType || typeof process.processType !== 'function') {
      return <TruncateTextToolTip text={process.ProcessType?.name || '-'} />;
    }
    return <TruncateTextToolTip text={process.processType().name || '-'} />;
  };

  const getProductLabel = useMemo(
    () => {
      // Si hay método custom (automatización), usarlo
      if (typeof process.customProduct === 'function') {
        return [process.customProduct()];
      }
      // Fallback al método original
      if (!process.entryLots || typeof process.entryLots !== 'function') {
        return [];
      }
      return truthty(process.entryLots())
        ? [
          ...new Set(
            process.entryLots().map((lot) => {
              const fruitName = lot?.fruit()?.name || t('NO SPECIES');
              const product = objects.products[lot.productId];
              const code = product?.code;
              return `${fruitName}|${code || ''}`;
            }),
          ),
        ]
        : [];
    },
    [process],
  );

  const getProduct = () => {
    if (!process.entryLots || typeof process.entryLots !== 'function' || !truthty(process.entryLots())) {
      return '-';
    }
    return (
      <Tooltip title={`${getProductLabel.join(', ')}`}>
        <img alt="icon" src={BoxIcon} className="MuiSvgIcon-root" />
      </Tooltip>
    );
  };

  const getVariety = () => {
    // Si hay método custom (automatización), usarlo
    if (typeof process.customVariety === 'function') {
      return <TruncateTextToolTip text={process.customVariety()} />;
    }
    // Fallback al método original
    if (!process.entryLots || typeof process.entryLots !== 'function' || !truthty(process.entryLots())) {
      return '-';
    }
    return (
      <TruncateTextToolTip
        text={[
          ...new Set(process.entryLots().map((lot) => lot?.variety()?.name || t('NO VARIETY'))),
        ].join(', ')}
      />
    );
  };

  const getProcessDate = () => {
    if (typeof process.dateLabel === 'function') {
      return process.dateLabel();
    }
    return <TruncateTextToolTip text={moment(process.date).format('DD-MM-YYYY')} />;
  };

  const getCreatedBy = () => {
    if (!process.createdByEmployee || typeof process.createdByEmployee !== 'function') {
      return <TruncateTextToolTip text="-" lines={1} />;
    }
    return <TruncateTextToolTip text={process.createdByEmployee()} lines={1} />;
  };

  const getEntry = () => {
    if (!process.entryLots || typeof process.entryLots !== 'function') {
      return <Chip label="N/A" size="small" className={classes.blueChip} />;
    }
    const label = `${fNumber.format(
      process.entryLots().reduce((acum, l) => acum + l.quantity, 0),
    )} ${unitCode}`;
    return (
      <Tooltip title={label} placement="top">
        <Chip
          icon={<ExitToAppIcon />}
          size="small"
          label={label}
          className={classes.blueChip}
          color="primary"
        />
      </Tooltip>
    );
  };

  const getExit = () => {
    if (!process.departureLots || typeof process.departureLots !== 'function') {
      return <Chip label="N/A" size="small" className={classes.greenChip} />;
    }
    const label = `${fNumber.format(
      process.departureLots().reduce((acum, l) => acum + l.quantity, 0),
    )} ${unitCode}`;
    return (
      <Tooltip title={label} placement="top">
        <Chip
          icon={<LaunchIcon />}
          size="small"
          label={label}
          className={classes.greenChip}
          color="primary"
        />
      </Tooltip>
    );
  };

  const getDecrease = () => {
    if (!process.entryLots || !process.departureLots ||
      typeof process.entryLots !== 'function' ||
      typeof process.departureLots !== 'function') {
      return <Chip label="N/A" size="small" className={classes.redChip} />;
    }
    const label = `${fNumber.format(
      process.entryLots().reduce((acum, l) => acum + l.quantity, 0) -
      process.departureLots().reduce((acum, l) => acum + l.quantity, 0),
    )} ${unitCode}`;
    return (
      <Tooltip title={label} placement="top">
        <Chip
          icon={<ErrorIcon />}
          size="small"
          label={label}
          className={classes.redChip}
          color="primary"
        />
      </Tooltip>
    );
  };

  const getOptions = () =>
    falsy(process.closedAt) ? (
      <Box>
        <IconButton
          data-id={process.id}
          onClick={(e) => {
            e.stopPropagation();
            openMenuHandler(e);
          }}>
          <MoreVertIcon />
        </IconButton>
      </Box>
    ) : null;

  const getArrayItem = () => [
    {
      value: getNumber(),
      // icon: <AssignmentOutlined />,
    },
    { value: getLots(), icon: null },
    { value: getSupplier(), icon: null },
    { value: getCategory(), icon: <ListIcon /> },
    { value: getProcessType(), icon: <CategoryOutlined /> },
    {
      value: getProduct(),
      // icon: <img src={BoxIcon} className="MuiSvgIcon-root" />,
    },
    { value: getVariety(), icon: null },
    {
      value: getProcessDate(),
      icon: <CalendarTodayOutlined style={{ fontSize: '1.1rem' }} />,
    },
    { value: getCreatedBy(), icon: <PersonOutline /> },
    { value: getEntry(), icon: null },
    { value: getExit(), icon: null },
    { value: getDecrease(), icon: null },
    { value: <Box className="row-options">{getOptions()}</Box>, icon: null },
  ];

  return (
    <div style={{ cursor: 'pointer' }}>
      <Row
        row={getArrayItem()}
        handleOnClick={processOnClick(process.id)}
        headerList={headerList}
        isMobileDevice={isMobileDevice}
      />
    </div>
  );
}

export default Item;
